import { ApolloQueryResult, FetchResult, gql } from '@apollo/client';
import { inject, injectable } from 'tsyringe';
import { PraGraphQlClient } from 'app/cross-cutting-concerns/communication/clients/PraGraphQlClient';
import type {
  AssociateDevicesToMapInput,
  AssociateDevicesToMapResponse,
  CleaningScheduleOccurrence,
  CleaningScheduleV2,
  CreateScheduleInput,
  DeleteScheduleInput,
  Maps,
  Map,
  MapGroups,
  PaginationMetadata,
  QueryCleaningScheduleOccurrencesArgs,
  QueryRoutinesArgs,
  QueryRoutesArgs,
  Route,
  Routine,
  QueryMapsArgs,
  QueryMapGroupsArgs,
  UpdateScheduleInput,
} from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';

export type ScheduleClientListCleaningScheduleOccurrencesOptions = QueryCleaningScheduleOccurrencesArgs;
export type ScheduleClientListRoutinesOptions = QueryRoutinesArgs;
export type ScheduleClientListRoutesOptions = QueryRoutesArgs;
export type ScheduleClientListMapsOptions = QueryMapsArgs;
export type ScheduleClientListAvailableMapsOptions = QueryMapsArgs;
export type ScheduleClientListMapGroupsOptions = QueryMapGroupsArgs;
export type ScheduleClientCreateScheduleOptions = CreateScheduleInput;
export type ScheduleClientUpdateScheduleOptions = UpdateScheduleInput;
export type ScheduleClientDeleteScheduleOptions = DeleteScheduleInput;
export type ScheduleClientAssociateDevicesToMapOptions = AssociateDevicesToMapInput;

// Narrower than the generated Route/Routine/Map types — only the fields these queries actually select.
export type ScheduleRoute = Pick<Route, 'id' | 'robotRouteId' | 'name' | 'duration' | 'postChargingDuration'>;
export type ScheduleRoutine = Pick<
  Routine,
  'id' | 'name' | 'anonymous' | 'duration' | 'postChargingDuration' | 'routeIds'
>;
export type ScheduleOccurrenceRoute = Omit<ScheduleRoute, 'robotRouteId'>;
export type ScheduleMap = Pick<Map, 'id' | 'name'>;
// The Map Assignment modal only ever reads these fields (see mapAssignment.ts) — unlike listMaps
// (the schedule-create map picker), it never needs work/jsonData/width/height, so it gets its own
// leaner query rather than paying for presigned-URL generation and map JSON it won't use.
export type AvailableMap = Pick<Map, 'id' | 'name' | 'thumbnail' | 'preview' | 'groupId'>;

export type ScheduleOccurrence = Omit<CleaningScheduleOccurrence, 'route' | 'routine' | 'map'> & {
  route?: ScheduleOccurrenceRoute | null;
  routine?: ScheduleRoutine | null;
  map?: ScheduleMap | null;
};

export interface CleaningScheduleOccurrencesResponse {
  cleaningScheduleOccurrences: ScheduleOccurrence[];
}

export interface RoutinesResponse {
  routines: { nodes: (ScheduleRoutine | null)[] };
}

export interface RoutesResponse {
  routes: { nodes: (ScheduleRoute | null)[] };
}

export interface MapsResponse {
  maps: Maps;
}

export interface AvailableMapsResponse {
  maps: { nodes: AvailableMap[]; pageInfo: PaginationMetadata };
}

export interface MapGroupsResponse {
  mapGroups: MapGroups;
}

export interface CreateScheduleResponse {
  createSchedule: CleaningScheduleV2;
}

export interface UpdateScheduleResponse {
  updateSchedule: CleaningScheduleV2;
}

export interface DeleteScheduleMutationResponse {
  deleteSchedule: CleaningScheduleV2;
}

export interface AssociateDevicesToMapMutationResponse {
  associateDevicesToMap: AssociateDevicesToMapResponse;
}

@injectable()
export class ScheduleClient {
  constructor(@inject('PraGraphQlClient') private praGraphQlClient: PraGraphQlClient) {}

  public listCleaningScheduleOccurrences = async ({
    materialNumber,
    serialNumber,
    period,
  }: ScheduleClientListCleaningScheduleOccurrencesOptions): Promise<
    ApolloQueryResult<CleaningScheduleOccurrencesResponse>
  > =>
    this.praGraphQlClient.query({
      query: gql`
        query CleaningScheduleOccurrences($materialNumber: String!, $serialNumber: String!, $period: PeriodInput!) {
          cleaningScheduleOccurrences(materialNumber: $materialNumber, serialNumber: $serialNumber, period: $period) {
            id
            scheduleId
            scheduleName
            startDate
            suction
            rrule
            cleaningMode
            cleaningZones
            map {
              id
              name
            }
            route {
              id
              name
              duration
              postChargingDuration
            }
            routine {
              id
              name
              anonymous
              duration
              postChargingDuration
              routeIds
            }
          }
        }
      `,
      variables: {
        materialNumber,
        serialNumber,
        period,
      },
      context: {
        skipApiCache: true,
      },
    });

  public listRoutines = async ({
    materialNumber,
    serialNumber,
    anonymous,
    deviceTypes,
    routeId,
    first,
    orderBy,
  }: ScheduleClientListRoutinesOptions): Promise<ApolloQueryResult<RoutinesResponse>> =>
    this.praGraphQlClient.query({
      query: gql`
        query Routines(
          $materialNumber: String
          $serialNumber: String
          $anonymous: Boolean
          $deviceTypes: [DeviceTypeName!]
          $routeId: Int
          $first: Int
          $orderBy: RoutineOrder
        ) {
          routines(
            materialNumber: $materialNumber
            serialNumber: $serialNumber
            anonymous: $anonymous
            deviceTypes: $deviceTypes
            routeId: $routeId
            first: $first
            orderBy: $orderBy
          ) {
            nodes {
              id
              name
              anonymous
              duration
              postChargingDuration
              routeIds
            }
          }
        }
      `,
      variables: { materialNumber, serialNumber, anonymous, deviceTypes, routeId, first, orderBy },
    });

  public listRoutes = async ({
    materialNumber,
    serialNumber,
    deviceTypes,
    first,
    orderBy,
  }: ScheduleClientListRoutesOptions): Promise<ApolloQueryResult<RoutesResponse>> =>
    this.praGraphQlClient.query({
      query: gql`
        query Routes(
          $materialNumber: String
          $serialNumber: String
          $deviceTypes: [DeviceTypeName!]
          $first: Int
          $orderBy: RouteOrder
        ) {
          routes(
            materialNumber: $materialNumber
            serialNumber: $serialNumber
            deviceTypes: $deviceTypes
            first: $first
            orderBy: $orderBy
          ) {
            nodes {
              id
              robotRouteId
              name
              duration
              postChargingDuration
            }
          }
        }
      `,
      variables: { materialNumber, serialNumber, deviceTypes, first, orderBy },
    });

  public listMaps = async ({
    deviceIds,
    filter,
    groupIds,
    mnsns,
  }: ScheduleClientListMapsOptions): Promise<ApolloQueryResult<MapsResponse>> =>
    this.praGraphQlClient.query({
      query: gql`
        query Maps($deviceIds: [String!], $filter: FilterInput, $groupIds: [String!], $mnsns: [MNSNsInput!]) {
          maps(deviceIds: $deviceIds, filter: $filter, groupIds: $groupIds, mnsns: $mnsns) {
            nodes {
              id
              name
              deviceId
              materialNumber
              serialNumber
              customerId
              groupId
              mapGroupName
              thumbnail
              preview
              image
              work
              function
              width
              height
              jsonData {
                jsonContent {
                  work
                  config
                  function
                }
              }
              associatedDevices {
                materialNumber
                serialNumber
              }
            }
            pageInfo {
              totalCount
              limit
              offset
              orderBy
              order
            }
          }
        }
      `,
      variables: { deviceIds, filter, groupIds, mnsns },
    });

  // For the Map Assignment modal — see AvailableMap's comment for why this excludes
  // work/jsonData/width/height (only ever read by the schedule-create map picker).
  public listAvailableMaps = async ({
    deviceIds,
    filter,
    groupIds,
    mnsns,
  }: ScheduleClientListAvailableMapsOptions): Promise<ApolloQueryResult<AvailableMapsResponse>> =>
    this.praGraphQlClient.query({
      query: gql`
        query AvailableMaps($deviceIds: [String!], $filter: FilterInput, $groupIds: [String!], $mnsns: [MNSNsInput!]) {
          maps(deviceIds: $deviceIds, filter: $filter, groupIds: $groupIds, mnsns: $mnsns) {
            nodes {
              id
              name
              groupId
              thumbnail
              preview
            }
            pageInfo {
              totalCount
              limit
              offset
              orderBy
              order
            }
          }
        }
      `,
      variables: { deviceIds, filter, groupIds, mnsns },
    });

  public listMapGroups = async ({
    filter,
  }: ScheduleClientListMapGroupsOptions): Promise<ApolloQueryResult<MapGroupsResponse>> =>
    this.praGraphQlClient.query({
      query: gql`
        query MapGroups($filter: FilterInput) {
          mapGroups(filter: $filter) {
            nodes {
              id
              name
              mapGroupCount
            }
            pageInfo {
              totalCount
              limit
              offset
              orderBy
              order
            }
          }
        }
      `,
      variables: { filter },
    });

  public createSchedule = async ({
    materialNumber,
    serialNumber,
    mapId,
    routeId,
    routineId,
    rrule,
    name,
    suction,
    cleaningZones,
  }: ScheduleClientCreateScheduleOptions): Promise<FetchResult<CreateScheduleResponse>> =>
    this.praGraphQlClient.mutate({
      mutation: gql`
        mutation CreateSchedule($input: CreateScheduleInput!) {
          createSchedule(input: $input) {
            cleaningMode
            cleaningZones
            createdAt
            deviceId
            deviceName
            firstExecution
            id
            lastExecution
            mapGroupName
            mapId
            mapName
            materialNumber
            name
            routeId
            routineId
            rrule
            serialNumber
            status
            suction
            type
            updatedAt
          }
        }
      `,
      variables: {
        input: {
          materialNumber,
          serialNumber,
          mapId,
          routeId,
          routineId,
          rrule,
          name,
          suction,
          cleaningZones,
        },
      },
    });

  public updateSchedule = async ({
    id,
    name,
    mapId,
    routineId,
    routeId,
    rrule,
    suction,
    cleaningZones,
    occurrence,
    applyToFollowing,
  }: ScheduleClientUpdateScheduleOptions): Promise<FetchResult<UpdateScheduleResponse>> =>
    this.praGraphQlClient.mutate({
      mutation: gql`
        mutation UpdateSchedule($input: UpdateScheduleInput!) {
          updateSchedule(input: $input) {
            cleaningMode
            cleaningZones
            createdAt
            deviceId
            deviceName
            firstExecution
            id
            lastExecution
            mapGroupName
            mapId
            mapName
            materialNumber
            name
            routeId
            routineId
            rrule
            serialNumber
            status
            suction
            type
            updatedAt
          }
        }
      `,
      variables: {
        input: {
          id,
          name,
          mapId,
          routineId,
          routeId,
          rrule,
          suction,
          cleaningZones,
          occurrence,
          applyToFollowing,
        },
      },
    });

  public deleteSchedule = async ({
    id,
    occurrence,
    applyToFollowing,
  }: ScheduleClientDeleteScheduleOptions): Promise<FetchResult<DeleteScheduleMutationResponse>> =>
    this.praGraphQlClient.mutate({
      mutation: gql`
        mutation DeleteSchedule($input: DeleteScheduleInput!) {
          deleteSchedule(input: $input) {
            cleaningMode
            cleaningZones
            createdAt
            deviceId
            deviceName
            firstExecution
            id
            lastExecution
            mapGroupName
            mapId
            mapName
            materialNumber
            name
            routeId
            routineId
            rrule
            serialNumber
            status
            suction
            type
            updatedAt
          }
        }
      `,
      variables: {
        input: {
          id,
          occurrence,
          applyToFollowing,
        },
      },
    });

  public associateDevicesToMap = async ({
    id,
    associatedDevices,
  }: ScheduleClientAssociateDevicesToMapOptions): Promise<FetchResult<AssociateDevicesToMapMutationResponse>> =>
    this.praGraphQlClient.mutate({
      mutation: gql`
        mutation AssociateDevicesToMap($input: AssociateDevicesToMapInput!) {
          associateDevicesToMap(input: $input) {
            success
          }
        }
      `,
      variables: {
        input: {
          id,
          associatedDevices,
        },
      },
    });
}
